/* eslint-disable */
import { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
// @ts-ignore - No TypeScript definitions available for this JS library
import WacomGSS from './lib/wgssStuSdk';

// Type definitions for WacomGSS SDK
interface WacomGSSProtocol {
    InkingMode: {
        InkingMode_Off: number;
        InkingMode_On: number;
    };
    EncodingFlag: {
        EncodingFlag_24bit: number;
        EncodingFlag_16bit: number;
    };
    EncodingMode: {
        EncodingMode_24bit_Bulk: number;
        EncodingMode_24bit: number;
        EncodingMode_16bit_Bulk: number;
        EncodingMode_16bit: number;
        EncodingMode_1bit: number;
    };
    ReportId: {
        ReportId_EncryptionStatus: number;
        ReportId_PenDataOptionMode: number;
    };
    PenDataOptionMode: {
        PenDataOptionMode_None: number;
        PenDataOptionMode_TimeCount: number;
        PenDataOptionMode_TimeCountSequence: number;
    };
}

interface WacomGSSTablet {
    setInkingMode: (mode: number) => Promise<any>;
    endCapture: () => Promise<any>;
    setClearScreen: () => Promise<any>;
    disconnect: () => Promise<any>;
    getInkThreshold: () => Promise<InkThreshold>;
    getCapability: () => Promise<Capability>;
    getInformation: () => Promise<any>;
    getProductId: () => Promise<number>;
    supportsWrite: () => Promise<boolean>;
    isSupported: (reportId: number) => Promise<boolean>;
    getDHprime: () => Promise<any>;
    setPenDataOptionMode: (mode: number) => Promise<any>;
    writeImage: (encodingMode: number, imageData: any) => Promise<any>;
    Constructor: (intf: any, null1: null, null2: null) => Promise<any>;
}

interface WacomGSSUsbInterface {
    Constructor: () => Promise<any>;
    connect: (device: UsbDevice, flag: boolean) => Promise<any>;
}

interface WacomGSSReportHandler {
    onReportPenData: ((report: PenDataReport) => void) | null;
    onReportPenDataOption: ((report: PenDataReport) => void) | null;
    onReportPenDataTimeCountSequence: ((report: PenDataReport) => void) | null;
    startReporting: (tablet: WacomGSSTablet, flag: boolean) => Promise<any>;
}

interface Rectangle {
    x: number;
    y: number;
    width: number;
    height: number;
    Contains: (pt: Point) => boolean;
}

interface VirtualButton {
    Bounds: Rectangle;
    Text: string;
    Click: (() => void) | null;
}

interface Point {
    x: number;
    y: number;
}

interface UsbDevice {
    idVendor: number;
    idProduct: number;
}

interface InkThreshold {
    onPressureMark: number;
    offPressureMark: number;
}

interface Capability {
    screenWidth: number;
    screenHeight: number;
    tabletMaxX: number;
    tabletMaxY: number;
    encodingFlag: number;
}

interface PenDataReport {
    x: number;
    y: number;
    pressure: number;
    shouldDrawPoint?: boolean;
}

interface SigCaptureProps {
    width: number;
    height: number;
    text: string;
    onCapture?: (imageSrc: string) => void;
}

let tablet: WacomGSSTablet | null;

export default function SigCapture({
    width,
    height,
    text,
    onCapture,
}: SigCaptureProps) {
    const { t } = useTranslation();
    let interval: NodeJS.Timeout | undefined;
    let m_btns: VirtualButton[]; // The array of buttons that we are emulating,
    let m_clickBtn = -1;
    let m_usbDevices: UsbDevice[];
    let m_capability: Capability;
    let m_inkThreshold: InkThreshold;
    let m_imgData: any;
    let m_encodingMode: number;
    let ctx: CanvasRenderingContext2D | null;
    let canvas: HTMLCanvasElement;
    let m_penData: PenDataReport[];
    let lastPoint: Point;
    let isDown: boolean;

    const [serviceReady, setServiceReady] = useState<boolean>(false);
    const [serviceFailure, setServiceFailure] = useState<boolean>(false);
    const [tabletReady, setTabletReady] = useState<boolean>(false);
    const [tabletFailure, setTabletFailure] = useState<boolean>(false);
    const [tableWorking, setTabletWorking] = useState<boolean>(false);
    const [tabletDone, setTableDone] = useState<boolean>(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);

    let waitCounter = 0;

    // Init: check for Wacom's local service
    useEffect(() => {
        interval = setInterval(checkForSigCaptX, 1000);
        return () => {
            clearInterval(interval);
        };
    });

    // If service was detected, check for device
    useEffect(() => {
        if (serviceReady) {
            waitCounter = 0;
            clearInterval(interval);
            interval = setInterval(checkForTablet, 1000);
        }

        return () => {
            clearInterval(interval);
        };
    }, [serviceReady]);

    // Image content modified
    useEffect(() => {
        if (imageSrc && onCapture && onCapture.constructor === Function) {
            onCapture(imageSrc);
        }
    }, [imageSrc]);

    useEffect(() => {
        if (serviceFailure) {
            clearInterval(interval);
        }
    }, [serviceFailure]);

    useEffect(() => {
        if (tabletFailure) {
            clearInterval(interval);
        }
    }, [tabletFailure]);

    // cleanup
    useEffect(() => btnCancel_Click, []);

    // Check for local service
    function checkForSigCaptX(): void {
        if (waitCounter >= 3) {
            setServiceFailure(true);
            return;
        }

        if (WacomGSS.STU.isServiceReady()) {
            setServiceReady(true);
        } else {
            waitCounter++;
        }
    }

    // Check for device
    function checkForTablet(): void {
        if (waitCounter >= 3) {
            setTabletFailure(true);
            return;
        }

        WacomGSS.STU.isDCAReady()
            .then((msg: boolean) => {
                if (!msg) {
                    return false;
                }
                WacomGSS.STU.onDCAtimeout = onDCAtimeout;
                return WacomGSS.STU.getUsbDevices();
            })
            .then((msg: UsbDevice[] | boolean) => {
                if (msg && Array.isArray(msg) && msg.length > 0) {
                    setTabletReady(true);
                } else {
                    waitCounter++;
                    setTabletReady(false);
                }
            });
    }

    function onDCAtimeout(): void {
        // Device Control App has timed-out and shut down
        setTimeout(close, 0);
    }

    function Rectangle(
        this: Rectangle,
        x: number,
        y: number,
        width: number,
        height: number,
    ) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;

        this.Contains = function (pt: Point): boolean {
            return (
                pt.x >= this.x &&
                pt.x <= this.x + this.width &&
                pt.y >= this.y &&
                pt.y <= this.y + this.height
            );
        };
    }

    // In order to simulate buttons, we have our own Button class that stores the bounds and event handler.
    // Using an array of these makes it easy to add or remove buttons as desired.
    //  delegate void ButtonClick();
    function VirtualButton(this: VirtualButton) {
        this.Bounds = new (Rectangle as any)(0, 0, 0, 0); // in Screen coordinates
        this.Text = '';
        this.Click = null;
    }

    function _Point(this: Point, x: number, y: number): void {
        this.x = x;
        this.y = y;
    }
    // Export for use in the file
    // @ts-ignore - Used as constructor at line 871
    const Point = _Point as any;

    function createCanvas(): void {
        canvas = document.createElement('canvas');
        canvas.id = 'myCanvas';
        canvas.width = width;
        canvas.height = height;
        ctx = canvas.getContext('2d');
    }

    function disconnect(): Promise<any> {
        // var deferred = Q.defer();

        return new Promise((resolve: any) => {
            // let tablet = getTablet();
            if (tablet) {
                const p = new WacomGSS.STU.Protocol() as WacomGSSProtocol;
                tablet
                    .setInkingMode(p.InkingMode.InkingMode_Off)
                    .then(() => tablet!.endCapture())
                    .then((message: any) => {
                        if (m_imgData) {
                            return m_imgData.remove();
                        }
                        return message;
                    })
                    .then(() => {
                        m_imgData = null;
                        return tablet!.setClearScreen();
                    })
                    .then(() => tablet!.disconnect())
                    .then(() => {
                        tablet = null;
                        // clear canvas
                        clearCanvas(canvas, ctx);
                    })
                    .then((message: any) => {
                        resolve(message);
                    })
                    .catch((err: any) => {
                        resolve(`disconnect error: ${err}`);
                    });
            } else {
                resolve(undefined);
            }
        });
    }

    window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
        const confirmationMessage = '';
        WacomGSS.STU.close();
        (e || window.event).returnValue = confirmationMessage; // Gecko + IE
        return confirmationMessage; // Webkit, Safari, Chrome
    });

    // Error-derived object for Device Control App not ready exception
    function DCANotReady(this: any, message?: string) {
        this.message = message;
    }
    DCANotReady.prototype = new Error();

    function beginCaptureProcess(): void {
        const p = new WacomGSS.STU.Protocol() as WacomGSSProtocol;
        let intf: WacomGSSUsbInterface;

        WacomGSS.STU.isDCAReady()
            .then((msg: boolean) => {
                if (!msg) {
                    throw new (DCANotReady as any)();
                }
                // Set handler for Device Control App timeout
                WacomGSS.STU.onDCAtimeout = onDCAtimeout;

                return WacomGSS.STU.getUsbDevices();
            })
            .then((message: UsbDevice[]) => {
                if (message == null || message.length == 0) {
                    throw new (DCANotReady as any)('No STU devices found');
                }
                m_usbDevices = message;
                return WacomGSS.STU.isSupportedUsbDevice(
                    m_usbDevices[0]?.idVendor,
                    m_usbDevices[0]?.idProduct,
                );
            })
            .then(() => {
                intf = new WacomGSS.STU.UsbInterface();
                return intf.Constructor();
            })
            .then(() => {
                if (!m_usbDevices[0]) {
                    throw new Error('No USB device available');
                }
                return intf.connect(m_usbDevices[0], true);
            })
            .then(() => {
                tablet = new WacomGSS.STU.Tablet();
                return tablet!.Constructor(intf, null, null);
            })
            .then(() => {
                intf = null as any;
                return tablet!.getInkThreshold();
            })
            .then((message: InkThreshold) => {
                m_inkThreshold = message;
                return tablet!.getCapability();
            })
            .then((message: Capability) => {
                m_capability = message;
                createCanvas();
                return tablet!.getInformation();
            })
            .then(() => tablet!.getInkThreshold())
            .then(() => tablet!.getProductId())
            .then((message: number) =>
                WacomGSS.STU.ProtocolHelper.simulateEncodingFlag(
                    message,
                    m_capability.encodingFlag,
                ),
            )
            .then((message: number) => {
                const encodingFlag = message;
                if ((encodingFlag & p.EncodingFlag.EncodingFlag_24bit) != 0) {
                    return tablet!.supportsWrite().then((message: boolean) => {
                        m_encodingMode = message
                            ? p.EncodingMode.EncodingMode_24bit_Bulk
                            : p.EncodingMode.EncodingMode_24bit;
                    });
                }
                if ((encodingFlag & p.EncodingFlag.EncodingFlag_16bit) != 0) {
                    return tablet!.supportsWrite().then((message: boolean) => {
                        m_encodingMode = message
                            ? p.EncodingMode.EncodingMode_16bit_Bulk
                            : p.EncodingMode.EncodingMode_16bit;
                    });
                }
                // assumes 1bit is available
                m_encodingMode = p.EncodingMode.EncodingMode_1bit;
            })
            .then(
                () => tablet!.isSupported(p.ReportId.ReportId_EncryptionStatus), // v2 encryption
            )
            .then(() => tablet!.getDHprime())
            .then(
                (dhPrime: any) =>
                    WacomGSS.STU.ProtocolHelper.supportsEncryption_DHprime(
                        dhPrime,
                    ), // v1 encryption
            )
            .then(() => tablet!.setClearScreen())
            .then(() =>
                tablet!.isSupported(p.ReportId.ReportId_PenDataOptionMode),
            )
            .then((message: boolean) => {
                if (message) {
                    return tablet!.getProductId().then((message: number) => {
                        let penDataOptionMode =
                            p.PenDataOptionMode.PenDataOptionMode_None;
                        switch (message) {
                            case WacomGSS.STU.ProductId.ProductId_520A:
                                penDataOptionMode =
                                    p.PenDataOptionMode
                                        .PenDataOptionMode_TimeCount;
                                break;
                            case WacomGSS.STU.ProductId.ProductId_430:
                            case WacomGSS.STU.ProductId.ProductId_530:
                                penDataOptionMode =
                                    p.PenDataOptionMode
                                        .PenDataOptionMode_TimeCountSequence;
                                break;
                            default:
                                console.log(
                                    'Unknown tablet supporting PenDataOptionMode, setting to None.',
                                );
                        }
                        return tablet!.setPenDataOptionMode(penDataOptionMode);
                    });
                }
                m_encodingMode = p.EncodingMode.EncodingMode_1bit;
                return m_encodingMode;
            })
            .then(() => {
                // BEGIN!
                addButtons();

                drawText(text);

                const canvasImage = canvas.toDataURL('image/jpeg');
                return WacomGSS.STU.ProtocolHelper.resizeAndFlatten(
                    canvasImage,
                    0,
                    0,
                    0,
                    0,
                    m_capability.screenWidth,
                    m_capability.screenHeight,
                    m_encodingMode,
                    1,
                    false,
                    0,
                    true,
                );
            })
            .then((message: any) => {
                m_imgData = message;
                return tablet!.writeImage(m_encodingMode, message);
            })
            .then(() => tablet!.setInkingMode(p.InkingMode.InkingMode_On))
            .then(() => {
                const reportHandler =
                    new WacomGSS.STU.ProtocolHelper.ReportHandler() as WacomGSSReportHandler;
                lastPoint = { x: 0, y: 0 };
                isDown = false;
                ctx!.lineWidth = 1;

                const penData = function (report: PenDataReport): void {
                    const shouldDrawPoint = processButtons(report, canvas);

                    processPoint(report, canvas, ctx, false);

                    m_penData.push({ ...report, shouldDrawPoint });
                };
                /* let penDataEncryptedOption = function (report) {
					//console.log("reportOp: " + JSON.stringify(report));
					m_penData.push(report.penData[0], report.penData[1]);
					processButtons(report.penData[0], canvas);
					processPoint(report.penData[0], canvas, ctx);
					processButtons(report.penData[1], canvas);
					processPoint(report.penData[1], canvas, ctx);
				}

				let log = function (report) {
					//console.log("report: " + JSON.stringify(report));
				}

				let decrypted = function (report) {
					//console.log("decrypted: " + JSON.stringify(report));
				} */
                m_penData = new Array();
                reportHandler.onReportPenData = penData;
                reportHandler.onReportPenDataOption = penData;
                reportHandler.onReportPenDataTimeCountSequence = penData;
                /* reportHandler.onReportPenDataEncrypted = penDataEncryptedOption;
				reportHandler.onReportPenDataEncryptedOption = penDataEncryptedOption;
				reportHandler.onReportPenDataTimeCountSequenceEncrypted = penData;
				reportHandler.onReportDevicePublicKey = log;
				reportHandler.onReportEncryptionStatus = log;
				reportHandler.decrypt = decrypted; */

                // STATE
                setTabletWorking(true);

                return reportHandler.startReporting(tablet!, true);
            })
            .catch((ex: any) => {
                if (ex instanceof DCANotReady) {
                    // Device Control App not detected
                    // Reinitialize and re-try
                    WacomGSS.STU.Reinitialize();
                    setTabletReady(false);
                    // setTimeout(beginCaptureProcess, 1000);
                } else {
                    alert(`${t('sigCapture.unexpectedError')}:\n${ex}`);
                    setTimeout(close, 0);
                }
            });
    }

    function addButtons(): void {
        m_btns = new Array(3);
        m_btns[0] = new (VirtualButton as any)();
        m_btns[1] = new (VirtualButton as any)();
        m_btns[2] = new (VirtualButton as any)();

        if (
            m_usbDevices[0]?.idProduct != WacomGSS.STU.ProductId.ProductId_300
        ) {
            // Place the buttons across the bottom of the screen.
            const w2 = m_capability.screenWidth / 3;
            const w3 = m_capability.screenWidth / 3;
            const w1 = m_capability.screenWidth - w2 - w3;
            const y = (m_capability.screenHeight * 6) / 7;
            const h = m_capability.screenHeight - y;

            if (m_btns[0])
                m_btns[0].Bounds = new (Rectangle as any)(0, y, w1, h);
            if (m_btns[1])
                m_btns[1].Bounds = new (Rectangle as any)(w1, y, w2, h);
            if (m_btns[2])
                m_btns[2].Bounds = new (Rectangle as any)(w1 + w2, y, w3, h);
        } else {
            // The STU-300 is very shallow, so it is better to utilise
            // the buttons to the side of the display instead.

            const x = (m_capability.screenWidth * 3) / 4;
            const w = m_capability.screenWidth - x;

            const h2 = m_capability.screenHeight / 3;
            const h3 = m_capability.screenHeight / 3;
            const h1 = m_capability.screenHeight - h2 - h3;

            if (m_btns[0])
                m_btns[0].Bounds = new (Rectangle as any)(x, 0, w, h1);
            if (m_btns[1])
                m_btns[1].Bounds = new (Rectangle as any)(x, h1, w, h2);
            if (m_btns[2])
                m_btns[2].Bounds = new (Rectangle as any)(x, h1 + h2, w, h3);
        }

        if (m_btns[0]) m_btns[0].Text = 'OK';
        if (m_btns[1]) m_btns[1].Text = t('sigCapture.clear');
        if (m_btns[2]) m_btns[2].Text = t('sigCapture.cancel');
        if (m_btns[0]) m_btns[0].Click = btnOk_Click;
        if (m_btns[1]) m_btns[1].Click = btnClear_Click;
        if (m_btns[2]) m_btns[2].Click = btnCancel_Click;
        clearCanvas(canvas, ctx);
        drawButtons();
    }

    /*
     * Pintar un texto partiéndolo en frases que encajen en el ancho de la pantalla.
     * El algoritmo suma palabras y las mide con canvas.context.measureText hasta que se supera el ancho y entonces crea una nueva frase.
     * */
    function drawText(text: string): void {
        ctx!.save();

        ctx!.strokeStyle = 'black';
        ctx!.font = '14px Arial';

        const words = text.split(' ');

        let phrase = '';
        let word: string | undefined;
        let index = 0;
        let line = 0;
        const xOffset = 10;
        const yOffset = 20;
        const lineHeight = 15;
        while ((word = words.shift())) {
            const predict = phrase + (index > 0 ? ' ' : '') + word;
            const metrics = ctx!.measureText(predict);

            if (metrics.width <= canvas.width - xOffset * 2) {
                phrase = predict;
                index++;
            } else {
                ctx!.fillText(phrase, xOffset, yOffset + lineHeight * line);
                phrase = '';
                words.unshift(word);
                line++;
                index = 0;
            }
        }

        ctx!.fillText(phrase, xOffset, yOffset + lineHeight * line);

        ctx!.restore();
    }

    function drawButtons(): void {
        ctx!.save();
        ctx!.setTransform(1, 0, 0, 1, 0, 0);

        ctx!.beginPath();
        ctx!.lineWidth = 1;
        ctx!.strokeStyle = 'black';
        ctx!.font = '22px Arial';

        // Draw the buttons
        for (let i = 0; i < m_btns.length; ++i) {
            const btn = m_btns[i];
            if (!btn) continue;
            // if (useColor)
            {
                ctx!.fillStyle = 'lightgrey';
                ctx!.fillRect(
                    btn.Bounds.x,
                    btn.Bounds.y,
                    btn.Bounds.width,
                    btn.Bounds.height,
                );
            }

            ctx!.fillStyle = 'black';
            ctx!.rect(
                btn.Bounds.x,
                btn.Bounds.y,
                btn.Bounds.width,
                btn.Bounds.height,
            );
            const xPos =
                btn.Bounds.x +
                (btn.Bounds.width / 2 - ctx!.measureText(btn.Text).width / 2);
            let yOffset: number;
            if (
                m_usbDevices[0]?.idProduct ==
                WacomGSS.STU.ProductId.ProductId_300
            )
                yOffset = 28;
            else if (
                m_usbDevices[0]?.idProduct ==
                WacomGSS.STU.ProductId.ProductId_430
            )
                // Esta es la nuestra
                yOffset = 22;
            else yOffset = 40;
            ctx!.fillText(btn.Text, xPos, btn.Bounds.y + yOffset);
        }
        ctx!.stroke();
        ctx!.closePath();

        ctx!.restore();
    }

    function clearScreen(): void {
        clearCanvas(canvas, ctx);
        drawButtons();
        m_penData = new Array();
        tablet!.writeImage(m_encodingMode, m_imgData);
    }

    function btnOk_Click(): void {
        // If there is at least one valid drawn point
        if (
            m_penData.reduce(
                (res: any, item: any) => res || item.shouldDrawPoint,
                false,
            )
        ) {
            generateImage();
            setTimeout(close, 0);

            setTableDone(true);
        }
    }

    function btnCancel_Click(): void {
        setTimeout(close, 0);

        setTableDone(false);
        setTabletWorking(false);
    }

    function btnClear_Click(): void {
        clearScreen();
    }

    function distance(a: Point, b: Point): number {
        return (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
    }

    function clearCanvas(
        in_canvas: HTMLCanvasElement,
        in_ctx: CanvasRenderingContext2D | null,
    ): void {
        if (!in_ctx) return;

        in_ctx.save();
        in_ctx.setTransform(1, 0, 0, 1, 0, 0);
        in_ctx.fillStyle = 'white';
        in_ctx.fillRect(0, 0, in_canvas.width, in_canvas.height);
        in_ctx.restore();
    }

    function processButtons(
        point: PenDataReport,
        in_canvas: HTMLCanvasElement,
    ): boolean {
        const nextPoint: Point = {
            x: Math.round(
                (in_canvas.width * point.x) / m_capability.tabletMaxX,
            ),
            y: Math.round(
                (in_canvas.height * point.y) / m_capability.tabletMaxY,
            ),
        };
        const isDown2 = isDown
            ? !(point.pressure <= m_inkThreshold.offPressureMark)
            : point.pressure > m_inkThreshold.onPressureMark;

        // trash point: discard
        if (nextPoint.x === 0 && nextPoint.y === 0) {
            return false;
        }

        let btn = -1;
        for (let i = 0; i < m_btns.length; ++i) {
            const button = m_btns[i];
            if (button?.Bounds.Contains(nextPoint)) {
                btn = i;
                break;
            }
        }

        if (isDown && !isDown2) {
            if (btn != -1 && m_clickBtn === btn) {
                m_btns[btn]?.Click?.();
            }
            m_clickBtn = -1;
        } else if (btn != -1 && !isDown && isDown2) {
            m_clickBtn = btn;
        }

        return btn == -1;
    }

    function processPoint(
        point: PenDataReport,
        in_canvas: HTMLCanvasElement,
        in_ctx: CanvasRenderingContext2D | null,
        ignore?: boolean,
    ): void {
        if (!in_ctx) return;

        const nextPoint: Point = {
            x: Math.round(
                (in_canvas.width * point.x) / m_capability.tabletMaxX,
            ),
            y: Math.round(
                (in_canvas.height * point.y) / m_capability.tabletMaxY,
            ),
        };

        const isDown2 = isDown
            ? !(point.pressure <= m_inkThreshold.offPressureMark)
            : point.pressure > m_inkThreshold.onPressureMark;

        if (!isDown && isDown2) {
            lastPoint = nextPoint;
        }

        if (
            !ignore &&
            ((isDown2 && distance(lastPoint, nextPoint) > 10) ||
                (isDown && !isDown2))
        ) {
            in_ctx.beginPath();
            in_ctx.moveTo(lastPoint.x, lastPoint.y);
            in_ctx.lineTo(nextPoint.x, nextPoint.y);
            in_ctx.stroke();
            in_ctx.closePath();
            lastPoint = nextPoint;
        }

        isDown = isDown2;
    }

    function generateImage(): void {
        const signatureCanvas = document.createElement('canvas');
        signatureCanvas.id = 'signatureCanvas';
        signatureCanvas.width = width;
        signatureCanvas.height = height;
        const signatureCtx = signatureCanvas.getContext('2d');

        clearCanvas(signatureCanvas, signatureCtx);
        signatureCtx!.lineWidth = 1;
        signatureCtx!.strokeStyle = 'black';
        lastPoint = { x: 0, y: 0 };
        isDown = false;

        for (let i = 0; i < m_penData.length; i++) {
            const penDataPoint = m_penData[i];
            if (penDataPoint && penDataPoint.shouldDrawPoint) {
                processPoint(penDataPoint, signatureCanvas, signatureCtx);
            }
        }

        setImageSrc(signatureCanvas.toDataURL('image/jpeg'));
    }

    function close(): void {
        setTabletWorking(false);

        // Clear handler for Device Control App timeout
        WacomGSS.STU.onDCAtimeout = null;

        disconnect()
            .then((msg: any) => {
                console.log('Disconnected', msg);
            })
            .catch((err: any) => {
                console.log(err);
            });
        // document.getElementsByTagName('body')[0].removeChild(modalBackground);
        // document.getElementsByTagName('body')[0].removeChild(formDiv);
    }

    /* function onCanvasClick(event: any) {
		// Enable the mouse to click on the simulated buttons that we have displayed.

		// Note that this can add some tricky logic into processing pen data
		// if the pen was down at the time of this click, especially if the pen was logically
		// also 'pressing' a button! This demo however ignores any that.

		var posX = event.pageX - formDiv.offsetLeft;
		var posY = event.pageY - formDiv.offsetTop;

		for (var i = 0; i < m_btns.length; i++) {
			if (m_btns[i].Bounds.Contains(new Point(posX, posY))) {
				m_btns[i].Click();
				break;
			}
		}
	} */

    return (
        <div>
            {serviceReady ? (
                <div>
                    {tabletDone ? (
                        <img
                            style={{ width, height }}
                            src={imageSrc || undefined}
                            alt="Signature"
                        />
                    ) : (
                        <Box
                            p={2}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            {tableWorking ? (
                                <Box
                                    flex={1}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Box mb={2}>
                                        <Typography variant="body1">
                                            {t('sigCapture.deviceBusy')}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className="button-delete"
                                        onClick={btnCancel_Click}
                                    >
                                        {t('sigCapture.cancel')}
                                    </Button>
                                </Box>
                            ) : (
                                <div>
                                    {tabletReady ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                                beginCaptureProcess();
                                            }}
                                        >
                                            {t('sigCapture.captureSignature')}
                                        </Button>
                                    ) : tabletFailure ? (
                                        <Typography variant="body1">
                                            {t('sigCapture.noDeviceDetected')}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body1">
                                            {t('sigCapture.waitingForDevice')}
                                        </Typography>
                                    )}
                                </div>
                            )}
                        </Box>
                    )}
                </div>
            ) : (
                <Box
                    p={2}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                >
                    {serviceFailure ? (
                        <Typography variant="body1">
                            {t('sigCapture.noServiceDetected')}
                        </Typography>
                    ) : (
                        <Typography variant="body1">
                            {t('sigCapture.waitingForService')}
                        </Typography>
                    )}
                </Box>
            )}
        </div>
    );
}
